import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { useSettings } from '../../hooks/useSettings';
import { SALE_COLUMNS, chosenColumns } from '../../utils/documentColumns';
import { addressLine, date, fine, money, payWords, rate, weight } from '../../utils/format';
import { ErrorNotice, Loading } from '../../components/ui';
import { Breakup, PrintDocument, PrintToolbar } from '../../components/print/PrintDocument';

interface InvoiceLine {
  line_no: number;
  item_code: string | null;
  product_name: string;
  // Sent by the server for every line; shown only where the shop bills with it.
  purity_code?: string | null;
  hsn_code?: string | null;
  stone_wt?: string;
  wastage_wt?: string;
  other_charges?: string;
  gross_wt: string;
  net_wt: string;
  fine_wt: string;
  rate_value: string;
  metal_value: string;
  making_amount: string;
  stone_amount: string;
  discount: string;
  taxable_value: string;
  image_data_uri?: string | null;
  cgst: string;
  sgst: string;
  igst: string;
  line_total: string;
}

interface Invoice {
  doc_no: string;
  doc_date: string;
  customer_name: string | null;
  walkin_name: string | null;
  customer_phone?: string | null;
  customer_address?: unknown;
  customer_gstin?: string | null;
  customer_pan?: string | null;
  place_of_supply?: string | null;
  is_interstate?: boolean;
  branch_name?: string;
  taxable_value: string;
  tax_total: string;
  round_off: string;
  grand_total: string;
  paid_amount: string;
  exchange_credit: string;
  cogs_amount?: string;
  gross_profit?: string;
  balance_amount: string;
  status: string;
  lines: InvoiceLine[];
  payments: { mode: string; amount: string; reference_no: string | null }[];
  /** What the customer still holds in advance with the shop, today. */
  advance_left?: string | null;
}

/**
 * The tax invoice. Weights, purity and rate are all on the paper, because a
 * jewellery customer checks the arithmetic and is entitled to.
 */
export function InvoicePrintPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const { data, error, isLoading } = useQuery({
    queryKey: ['sale', id],
    queryFn: () => api.get<Invoice>(`/sales/${id}`),
  });
  const { setting } = useSettings();
  const { header } = usePrintHeader(data?.branch_name);
  const { can } = usePermission();

  if (isLoading) return <Loading what="Preparing the invoice" />;
  if (error) return <ErrorNotice error={error} />;
  if (!data) return null;

  // A document that arrives without its lines still prints its header and
  // totals rather than emptying the screen.
  const lines = data.lines ?? [];
  const cgst = lines.reduce((a, l) => a + Number(l.cgst), 0);
  const sgst = lines.reduce((a, l) => a + Number(l.sgst), 0);
  const igst = lines.reduce((a, l) => a + Number(l.igst), 0);

  /**
   * The bill prints what the shop bills with.
   *
   * A shop that quotes wastage wants it on the customer's copy; one that folds
   * it into making does not want an empty column. Cost and profit are the
   * shop's own business and never print, whoever is looking at the screen.
   */
  const printCols = chosenColumns(
    setting('documents.sale_columns', SALE_COLUMNS.map((c) => c.key).join(',')),
    SALE_COLUMNS,
  ).filter((c) => !['item', 'total', 'cost', 'profit'].includes(c.key));

  const chosen = new Set(printCols.map((c) => c.key));
  const shows = (key: string): boolean => chosen.has(key);

  /** The figures behind a line's price, each labelled the way a bill reads. */
  const billBreakup = (l: InvoiceLine): { key: string; label: string; value: string }[] => {
    const gst = Number(l.cgst ?? 0) + Number(l.sgst ?? 0) + Number(l.igst ?? 0);
    return [
      { key: 'grossWt', label: 'Gross', value: weight(l.gross_wt) },
      { key: 'stoneWt', label: 'Stone wt', value: weight(l.stone_wt ?? '0') },
      { key: 'metal', label: 'Metal', value: money(l.metal_value) },
      { key: 'wastage', label: 'Wastage', value: fine(l.wastage_wt ?? '0') },
      { key: 'making', label: 'Making', value: money(l.making_amount) },
      { key: 'stoneAmount', label: 'Stones', value: money(l.stone_amount ?? '0') },
      { key: 'otherCharges', label: 'Other', value: money(l.other_charges ?? '0') },
      { key: 'discount', label: 'Less discount', value: money(l.discount ?? '0') },
      { key: 'tax', label: 'GST', value: money(gst.toFixed(2)) },
    ].filter((part) => {
      // A charge of nothing is left off the bill rather than printed as ₹0.00.
      const n = Number(part.value.replace(/[^\d.-]/g, ''));
      return part.key === 'grossWt' || n !== 0;
    });
  };

  const advanceUsed = (data.payments ?? [])
    .filter((p) => p.mode === 'ADVANCE')
    .reduce((a, p) => a + Number(p.amount), 0);
  const totals = [
    { label: 'Taxable value', value: money(data.taxable_value) },
    ...(cgst > 0 ? [{ label: 'CGST', value: money(cgst.toFixed(2)) }] : []),
    ...(sgst > 0 ? [{ label: 'SGST', value: money(sgst.toFixed(2)) }] : []),
    ...(igst > 0 ? [{ label: 'IGST', value: money(igst.toFixed(2)) }] : []),
    ...(Number(data.round_off) !== 0 ? [{ label: 'Round off', value: money(data.round_off) }] : []),
    ...(Number(data.exchange_credit) > 0
      ? [{ label: 'Less: old gold exchanged', value: money(data.exchange_credit), strong: true }]
      : []),
  ];

  return (
    <>
      <PrintToolbar onBack={() => navigate('/sales')} fileName={data.doc_no} />

      {/* Internal only: the shop's cost and what this invoice made. It never
          prints — a customer's bill does not carry the shop's margin — and it
          is shown only to a role allowed to see profit. */}
      {can('sales.view_profit') && data.gross_profit !== undefined ? (
        <div className="no-print mb-4 flex flex-wrap items-center gap-x-8 gap-y-1 rounded-md border border-silver-100 bg-surface px-4 py-2.5 text-sm shadow-card">
          <span className="text-2xs uppercase tracking-wide text-ink-soft">For the shop</span>
          <span>
            <span className="text-ink-soft">Sale value </span>
            <span className="num font-medium">{money(data.taxable_value)}</span>
          </span>
          <span>
            <span className="text-ink-soft">Cost </span>
            <span className="num font-medium">{money(data.cogs_amount)}</span>
          </span>
          <span>
            <span className="text-ink-soft">Profit </span>
            <span className={`num font-semibold ${Number(data.gross_profit) < 0 ? 'text-danger' : 'text-success'}`}>
              {money(data.gross_profit)}
            </span>
          </span>
          {Number(data.taxable_value) > 0 ? (
            <span className="num text-2xs text-ink-faint">
              {((Number(data.gross_profit) / Number(data.taxable_value)) * 100).toFixed(1)}% margin
            </span>
          ) : null}
        </div>
      ) : null}

      <PrintDocument
        title={data.status === 'CANCELLED' ? 'Tax invoice (cancelled)' : 'Tax invoice'}
        header={header}
        docNo={data.doc_no}
        docDate={date(data.doc_date)}
        party={{
          name: data.customer_name ?? data.walkin_name ?? 'Walk-in customer',
          phone: data.customer_phone,
          address: addressLine(data.customer_address) || null,
          gstin: data.customer_gstin,
          pan: data.customer_pan,
        }}
        partyLabel="Billed to"
        details={[
          { label: 'Place of supply', value: data.place_of_supply ?? '' },
          { label: 'Supply', value: data.is_interstate ? 'Inter-state (IGST)' : 'Within the state (CGST + SGST)' },
          { label: 'Pieces', value: String(lines.length) },
          {
            label: 'Net weight',
            value: weight(lines.reduce((a, l) => a + Number(l.net_wt), 0).toFixed(3)),
          },
        ]}
        totals={totals}
        grandTotal={data.grand_total}
        paymentMethod={
          data.payments.length > 0
            ? data.payments
                .map((p) => `${payWords(p.mode)} ${money(p.amount)}${p.reference_no ? ` (${p.reference_no})` : ''}`)
                .join(',  ')
            : undefined
        }
        footNote={[
          Number(data.balance_amount) > 0 ? `Balance outstanding: ${money(data.balance_amount)}.` : 'Paid in full.',
          advanceUsed > 0
            ? `Advance adjusted on this bill: ${money(advanceUsed.toFixed(2))}. Advance still held for you: ${money(data.advance_left ?? '0')}.`
            : Number(data.advance_left ?? 0) > 0
              ? `Advance held for you with the shop: ${money(data.advance_left!)}.`
              : '',
          setting('documents.invoice_terms'),
        ]
          .filter(Boolean)
          .join('\n')}
      >
        {/* A jewellery line has more figures than a printed row can hold
            legibly, so the piece is one row and how its price was reached sits
            in a line beneath — the way a jeweller's own bill reads, not fifteen
            columns crushed edge to edge. */}
        <table className="doc-table">
          <thead>
            <tr>
              <th className="w-6">#</th>
              <th>Item</th>
              <th className="c">Purity</th>
              <th className="r">Net wt</th>
              <th className="r">Fine wt</th>
              <th className="r">Rate / g</th>
              <th className="r">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.line_no}>
                <td className="num !text-left">{l.line_no}</td>
                <td>
                  <span className="flex items-start gap-2">
                    {l.image_data_uri ? (
                      <img src={l.image_data_uri} alt="" className="h-9 w-9 shrink-0 rounded object-cover" />
                    ) : null}
                    <span className="min-w-0">
                      <span className="font-medium">{l.product_name}</span>
                      {l.item_code || l.hsn_code ? (
                        <span className="block font-mono text-[11px] text-ink-soft">
                          {[l.item_code, l.hsn_code ? `HSN ${l.hsn_code}` : null].filter(Boolean).join('  ·  ')}
                        </span>
                      ) : null}
                      {/* The build-up, in the shop's own words and only what it
                          billed with — an empty charge is left out rather than
                          printed as a zero. */}
                      <Breakup
                        parts={billBreakup(l)
                          .filter((part) => shows(part.key))
                          .map((part) => `${part.label} ${part.value}`)}
                      />
                    </span>
                  </span>
                </td>
                <td className="c num font-semibold">{l.purity_code ?? '—'}</td>
                <td className="num">{weight(l.net_wt)}</td>
                <td className="num">{fine(l.fine_wt)}</td>
                <td className="num">{rate(l.rate_value)}</td>
                <td className="num font-semibold">{money(l.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PrintDocument>
    </>
  );
}
